import {PayPalButtons, usePayPalScriptReducer} from "@paypal/react-paypal-js"
import {getFromSessionStorage, saveToSessionStorage} from "../functions/Crypto.tsx"
import {Box, Typography} from "@mui/material"
import {useContext, useEffect, useState} from "react"
import OrderContext from "../contexts/OrderContext.tsx"
import CartContext from "../contexts/CartContext.tsx"
import {useNavigate} from "react-router-dom"
import {useTranslation} from "react-i18next";
import {SupportedLanguages} from "../models/Languages.ts";
import i18n from "i18next";

const secretKey = import.meta.env.VITE_SEESION_SECRET_KEY

interface IProps {
    formRef: any
    onSubmit: () => void
}

const PayPalPayment = ({formRef, onSubmit}: IProps) => {
    const {
        setOrderId,
        createOrderMutation,
        errorCreatingOrder,
        updateOrderMutation,
        errorUpdatingOrder,
        finishCheckoutMutation,
        errorFinishCheckout,
        sendConfirmationMail,
        selectedCurrency,
        rates
    } = useContext(OrderContext)
    const {cancelReservationMutation} = useContext(CartContext)
    const [errorMessage, setErrorMessage] = useState<string>()
    const currentLanguage: SupportedLanguages = i18n.language as SupportedLanguages;
    const navigate = useNavigate()
    const {t} = useTranslation()

    const [{isPending, options}, dispatch] = usePayPalScriptReducer();

    useEffect(() => {
        dispatch({
            type: "resetOptions" as any,
            value: {
                ...options,
                currency: selectedCurrency,
            },
        });
    }, [selectedCurrency]);

    if (isPending) return <div>Loading PayPal...</div>;

    return (
        <Box>
            <PayPalButtons
                fundingSource='paypal'
                onClick={async (_data, actions) => {
                    const isValid = formRef.current?.reportValidity()
                    if (!isValid) {
                        return actions.reject()
                    }
                    let order = getFromSessionStorage('order', secretKey)
                    if (!order._id) {
                        order = await createOrderMutation.mutateAsync(order)
                        if (errorCreatingOrder) {
                            return actions.reject()
                        }
                    } else {
                        order = await updateOrderMutation.mutateAsync(order)
                        if (errorUpdatingOrder) {
                            return actions.reject()
                        }
                    }
                    saveToSessionStorage('order', order, secretKey)
                    return actions.resolve()
                }}
                createOrder={(_data, actions) => {
                    const order = getFromSessionStorage("order", secretKey);
                    const baseAmount = order.totalPrice * rates[selectedCurrency] + order.shippingCost * rates[selectedCurrency];

                    const value =
                        selectedCurrency === "JPY"
                            ? Math.round(baseAmount).toString()
                            : baseAmount.toFixed(2);

                    return actions.order.create({
                        intent: "CAPTURE",
                        purchase_units: [
                            {
                                amount: {
                                    currency_code: selectedCurrency,
                                    value,
                                },
                            },
                        ],
                        application_context: {
                            shipping_preference: "NO_SHIPPING",
                            user_action: "PAY_NOW",
                        },
                    });
                }}
                onApprove={async (_data, actions) => {
                    if (!actions.order) {
                        throw new Error("Order actions are unavailable.")
                    }
                    return actions.order.capture().then(async () => {
                        const order = getFromSessionStorage('order', secretKey)
                        cancelReservationMutation.mutate()
                        finishCheckoutMutation.mutate(order._id)
                        if (errorFinishCheckout) {
                            throw new Error('Finish checkout failed')
                        }

                        sendConfirmationMail(order._id, currentLanguage, order.shippingAddress.email)
                        navigate(`/order/${order._id}`)
                        setOrderId(undefined)
                        onSubmit()
                    })
                }}
                onError={(err: any) => {
                    console.error('PayPal Error: ', err)
                    setErrorMessage(t('PAYPAL_PAYMENT_ERROR_TEXT'))
                }}
            />
            {errorMessage && (
                <Typography color="error" sx={{marginBottom: '10px'}}>
                    {errorMessage}
                </Typography>
            )}
        </Box>
    )
}

export default PayPalPayment;